/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback } from 'react'; // add useEffect
import PropTypes from 'prop-types';
import { DialogsProvider, ToastProvider } from '../../../providers';
import { withContextProvider } from '../../../hoc';
import Tabs from '../../../componentsV2/Tabs/Tabs';
import CustomFieldsDisplay from '../../../componentsV2/CustomFields/CustomFieldsDisplay';
import HeaderWithBreadcrumbs from '../../../componentsV2/Headers/HeaderWithBreadcrumbs';
import { useUrlModals } from '../../../utils/useUrlModals';
import { getModelForm } from '../../../utils/api/modelForm';
import NotFound from '../../NotFound/NotFound';

const CustomFieldsLandingPage = ({ organizationId }) => {
  const orgId = organizationId;

  const breadcrumbLinks = [
    { href: '/admin', name: 'Admin' },
    { href: '/custom_fields', name: 'Custom Fields' },
  ];

  const TABS = [
    { title: 'Sites', name: 'site', content: [] },
    { title: 'Visits', name: 'visit', content: [] },
    { title: 'Contacts', name: 'contact', content: [] },
  ];

  const [activeTab, setActiveTab] = useState(() => {
    const tabName = new URLSearchParams(window.location.search).get('tab');
    const initialTab = TABS.find(tab => tab.name === tabName);

    return initialTab?.title || TABS[0]?.title || '';
  });
  const [error, setError] = useState('');
  const [modelId, setModelId] = useState('');
  const [formItems, setFormItems] = useState([]);
  const [initialFormItems, setInitialFormItems] = useState([]);
  const [isValidField, setIsValidField] = useState(true);

  const { modals, openModal } = useUrlModals({
    tab: { isIdParam: true },
    form: { isIdParam: true },
  });

  const onTabClick = (e, title) => {
    e.preventDefault();
    setActiveTab(title);

    const match = TABS.find(tab => tab.title === title);
    if (match) {
      openModal('tab', match.name);
    }
  };

  const activeTabName = TABS.find(tab => tab.title === activeTab)?.name || '';

  useEffect(() => {
    const initialized = ('tab' in modals) || ('form' in modals);
    if (!initialized) return;

    const noParamsYet = !modals.tab?.isOpen && !modals.form?.isOpen;
    if (noParamsYet) openModal('tab', 'site');
  }, [modals, openModal]);

  useEffect(() => {
    if (modals.tab?.value && modals.tab?.isOpen) {
      const match = TABS.find(tab => tab.name === modals.tab.value);
      if (match) {
        setActiveTab(match.title);
      }
    }
  }, [modals.tab]);

  // Fetch custom fields from the API
  const fetchCustomFields = useCallback(async () => {
    try {
      if (!orgId) throw new Error('Organization ID is missing');
      const response = await getModelForm(activeTabName, orgId);
      const data = await response.json();

      // Check if editCustomField id from URL belongs to this org's custom fields
      const urlParams = new URLSearchParams(window.location.search);
      const editCustomField = urlParams.get('editCustomField');
      // Check whether the field exists in current org's custom fields
      const fieldExists = data.form_items?.some(item => String(item.id) === String(editCustomField));
      if (editCustomField && !fieldExists) {
        setIsValidField(false);
      } else {
        setModelId(data.id || '');
        setFormItems(data.form_items || []);
        setInitialFormItems(data.form_items || []);
      }
    } catch (err) {
      setError(err.message);
    }
  }, [orgId, activeTabName]);

  return (
    isValidField ? (
      <DialogsProvider>
        <div className="ml-2.75 mr-6.5">
          <header>
            <HeaderWithBreadcrumbs links={breadcrumbLinks} name="custom_fields" label="Custom Fields" size="h-9" headingTestId="custom-fields-page-header" />
          </header>
          <p className="mt-4 max-w-[950px] break-words text-lg">
            Vamos includes default standard fields for essential information. Add custom fields to capture additional data specific to your organization. Any custom field you add here will be present for everyone in your organization.
          </p>

          {/* Tabs Component */}
          <div className="flex mt-11">
            <Tabs
              tabsData={TABS}
              onClick={onTabClick}
              activeTab={activeTab}
            />
          </div>

          {/* Content inside the selected tab */}
          <div className="bg-vamos-white border border-1 border-vamos-blue-gray rounded-md p-7 mt-6.5 text-lg text-vamos-gray">
            <div className="pr-2">
              <CustomFieldsDisplay
                fetchCustomFields={fetchCustomFields}
                formItems={formItems}
                setFormItems={setFormItems}
                initialFormItems={initialFormItems}
                modelId={modelId}
                error={error}
                setInitialFormItems={setInitialFormItems}
                orgId={String(orgId)}
                activeTabName={activeTabName}
              />
            </div>
          </div>
        </div>
      </DialogsProvider>
    ) : <NotFound />
  );
};

CustomFieldsLandingPage.propTypes = {
  organizationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default withContextProvider(ToastProvider)(CustomFieldsLandingPage);
